/* ---------------------------------------------------------------------------
   The desk start page: the register as a whole.

   THE HOME PAGE IS ABOUT THE LIST OF BRIDGES, and nothing about any one of
   them. The headline figures across the top, the register with its filters, and
   the network the structures sit on — the map, the roads by chainage and the
   districts. Everything specific to one structure is in the four applications,
   which the nav pane lists and every register row opens.

   Every figure is a rollup of the register itself plus the defect log. Nothing
   is invented: where a number cannot be computed from what is on file the tile
   says so rather than showing a zero.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import type { State } from '../core/store.ts';
import { statTile } from '../components/widgets.ts';
import { allBridges, kpis, locateDefects } from '../data/derive.ts';
import { consequenceCoverage } from '../data/consequence.ts';
import type { BridgeView } from '../data/types.ts';
import { operationalStatus } from '../data/posting.ts';
import { isOpenStatus } from '../data/defects.ts';
import { registerPanel } from './register.ts';
import { networkPanel } from './network.ts';

/* --------------------------- headline figures --------------------------- */

function headline(s: Readonly<State>, list: BridgeView[]): HTMLElement {
  /* One list from one query — see registerDefects in src/core/store.ts. */
  const open = locateDefects(s.registerDefects, list).filter((d) => isOpenStatus(d.status)).length;
  const reachable = s.backend === 'live';
  const coverage = consequenceCoverage(list);

  return h(
    'div',
    { class: 'kpis' },

    ...kpis(list).map((k) =>
      statTile({ label: k.label, value: k.value, unit: k.unit }),
    ),

    statTile({
      label: 'Open issues',
      // A dash rather than a zero where the register could not be reached:
      // "none logged" and "we could not ask" are different answers.
      value: reachable ? String(open) : '—',
      unit: 'items',
    }),

    /* Whether the network can be crossed (LC-23). */
    statTile({
      label: 'Posted',
      value: String(list.filter((b) => operationalStatus(b).kind === 'posted').length),
      unit: `of ${list.length}`,
    }),

    /* What a closure costs, network-wide: how many records can be prioritised
       on consequence at all, rather than an average over the handful that can. */
    statTile({
      label: 'Consequence data',
      value: String(coverage.recorded),
      unit: `of ${coverage.total}`,
    }),
  );
}

/* --------------------------- screen --------------------------- */

/**
 * The home page.
 *
 * The figures first, because they are the one-line answer to "how is the
 * network". Then the register, because a reader who came to find one structure
 * should find the list of them before anything else. Then the network, which is
 * the same list drawn on the ground.
 */
export function overviewScreen(s: Readonly<State>): HTMLElement {
  const list = allBridges();

  return h(
    'div',
    { class: ['home', s.homeView !== 'both' && `home--${s.homeView}`] },
    headline(s, list),
    s.homeView !== 'network' ? registerPanel(s) : null,
    s.homeView !== 'register' ? networkPanel(s) : null,
  );
}
